import React, { useState, useEffect, useRef } from 'react';
import MunicipalitySelector from './MunicipalitySelector';
import YearSelector from './YearSelector';
import MonthSelector from './MonthSelector';
import ChartSelector from './ChartSelector';
import HourlyBarChart from '../charts/HourlyBarChart';
import PercentageChangeChart from '../charts/PercentageChangeChart';
import { fetchConsumptionData, calculateProportionPerHour , compareProportionPerHour, calculateMonthlyLoadProfile, calculateAveragePerHour, getLatestYearMonth } from '../api/dataHandler';
import MonthlyLoadLineChart from '../charts/MonthlyLoadLineChart';
import { MONTHS } from '../data/constants';
import '../css/ChartContainer.css';
import { DEFAULT_CONFIG } from '../config/defaultConfig';
const MONTH_MAP = Object.fromEntries(MONTHS.map((m, i) => [m, i + 1]));

const ChartContainer = () => {
  const isFirstRender = useRef(true);
  const [showInfo, setShowInfo] = useState(false);
  const infoRef = useRef(null);
  const [municipality, setMunicipality] = useState(DEFAULT_CONFIG.municipality);
  const [yearBefore, setYearBefore] = useState(DEFAULT_CONFIG.yearBefore);
  const [yearAfter, setYearAfter] = useState(DEFAULT_CONFIG.yearAfter);
  const [monthFrom, setMonthFrom] = useState(DEFAULT_CONFIG.monthFrom);
  const [monthTo, setMonthTo] = useState(DEFAULT_CONFIG.monthTo);
  const [hourlyData, setHourlyData] = useState([]);
  const [dataBefore, setDataBefore] = useState([]);
  const [dataAfter, setDataAfter] = useState([]);
  const [valueTypeMonthly, setValueTypeMonthly] = useState(DEFAULT_CONFIG.valueTypeMonthly); // or "averageHourlyTotalConsumption"
  const [valueTypeByHour, setValueTypeByHour] = useState(DEFAULT_CONFIG.valueTypeByHour); // or "averageHourlyTotalConsumption"
  const [averageDiffPerHour, setAverageDiffPerHour] = useState([]);
  const [proportionChangePerHourPercent, setProportionChangePerHourPercent] = useState([]);
  const [percentChangeData, setPercentChangeData] = useState([]);
  const [proportionsBefore, setProportionsBefore] = useState([]);
  const [proportionsAfter, setProportionsAfter] = useState([]);
  const [averagesBefore, setAveragesBefore] = useState([]);
  const [averagesAfter, setAveragesAfter] = useState([]);
  const [chartType, setChartType] = useState(DEFAULT_CONFIG.chartType); // or "monthly"
  const [monthlyData, setMonthlyData] = useState([]);
  const municipalityMap = {
    "Aalborg": 851,
    "Hjørring": 860,
    "Jammerbugt": 849,
    "Frederikshavn": 813,
    "Brønderslev-Dronninglund": 810
  };

useEffect(() => {
  // if (isFirstRender.current) {
  //   isFirstRender.current = false;
  //   return;
  // }

    const fetchAndCompare = async () => {
  const from = MONTH_MAP[monthFrom];
  const to = MONTH_MAP[monthTo];
  const startMonth = String(from).padStart(2, '0');
  const endMonth = String(to).padStart(2, '0');

  const startBefore = `${yearBefore}-${startMonth}-01`;
  const endBefore = `${yearBefore}-${endMonth}-30`;

  const startAfter = `${yearAfter}-${startMonth}-01`;
  const endAfter = `${yearAfter}-${endMonth}-30`;

  const munNo = municipalityMap[municipality];

  const fetchedBefore = await fetchConsumptionData({
    municipalityNo: munNo,
    startDate: startBefore,
    endDate: endBefore,
  });

  const fetchedAfter = await fetchConsumptionData({
    municipalityNo: munNo,
    startDate: startAfter,
    endDate: endAfter,
  });

  // Compute proportion change
  const proportionChange = compareProportionPerHour(fetchedBefore, fetchedAfter);
  setProportionChangePerHourPercent(proportionChange);


  //set proportions before/after
    // Set grouped bar chart data
  setProportionsBefore(calculateProportionPerHour(fetchedBefore));
  setProportionsAfter(calculateProportionPerHour(fetchedAfter));
  setAveragesBefore(calculateAveragePerHour(fetchedBefore));
  setAveragesAfter(calculateAveragePerHour(fetchedAfter));
  // Compute average hourly kWh difference
  const avgBefore = calculateAveragePerHour(fetchedBefore);
  const avgAfter = calculateAveragePerHour(fetchedAfter);
  const avgDiff = avgAfter.map((v, i) => v - avgBefore[i]);
  setAverageDiffPerHour(avgDiff);
};

    fetchAndCompare();
  }, [municipality, yearBefore, yearAfter, monthFrom, monthTo, chartType]);


useEffect(() => {
  const fetchAndCompare = async () => {
    const munNo = municipalityMap[municipality];

    const rawData = await fetchConsumptionData({
      municipalityNo: munNo,
      startDate: `${yearBefore}-01-01`,
      endDate: `${yearAfter}-12-31`,
    });

    // Determine second-to-last complete month
    const today = new Date();
    today.setDate(1); // First of the month
    today.setMonth(today.getMonth() - 1); // Go back two months
    const cutoffDate = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
    };

    const filteredData = rawData.filter((entry) => {
      const date = new Date(entry.HourDK);
      const y = date.getFullYear();
      const m = date.getMonth() + 1;

      if (y < yearBefore || y > yearAfter) return false;
      if (y > cutoffDate.year) return false;
      if (y === cutoffDate.year && m > cutoffDate.month) return false;

      return true;
    });

    const monthHourCounts = {};

    filteredData.forEach((entry) => {
      const dt = new Date(entry.HourDK);
      const year = dt.getFullYear();
      const month = dt.getMonth() + 1;
      const key = `${year}-${String(month).padStart(2, '0')}`;

      if (!monthHourCounts[key]) monthHourCounts[key] = 0;
      monthHourCounts[key]++;
    });

    const monthlyLoad = calculateMonthlyLoadProfile(filteredData);

    // Debug duplicate timestamps
    const seen = new Set();
    let duplicateCount = 0;
    filteredData.forEach((d) => {
      const key = d.HourDK;
      if (seen.has(key)) duplicateCount++;
      seen.add(key);
    });

    setMonthlyData(monthlyLoad);
  };

  fetchAndCompare();
}, [municipality, yearBefore, yearAfter, chartType]);

useEffect(() => {
  function handleClickOutside(event) {
    if (infoRef.current && !infoRef.current.contains(event.target)) {
      setShowInfo(false);
    }
  }

  if (showInfo) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [showInfo]);

useEffect(() => {
  const handleBlur = () => setShowInfo(false);

  window.addEventListener('blur', handleBlur);

  return () => {
    window.removeEventListener('blur', handleBlur);
  };
}, []);


const renderInfoText = () => {
  if (chartType === "percentage" && valueTypeByHour === "averageHourlyTotalConsumption") {
    return (
      <>
        <strong>ℹ️ Gennemsnitligt timeforbrug (kWh):</strong>
        <p>
          Grafen viser det gennemsnitlige elforbrug i hver time af døgnet, sammenlignet mellem <strong>{yearBefore}</strong> og <strong>{yearAfter}</strong>, i perioden <strong>{monthFrom.toLowerCase()}</strong> til <strong>{monthTo.toLowerCase()}</strong> for hvert år.
        </p>
        <p>
          Hver time er repræsenteret med to søjler - én for hvert år - så du kan se, om forbruget typisk er steget, faldet eller uændret i den pågældende time.
        </p>
      </>
    );
  }

  if (chartType === "percentage" && valueTypeByHour === "averageHourlyProportionConsumption") {
    return (
      <>
        <strong>ℹ️ Andel af forbrug fordelt over døgnet (%):</strong>
        <p>
          Grafen viser, hvordan elforbruget i gennemsnit fordeler sig over døgnet i hhv. <strong>{yearBefore}</strong> og <strong>{yearAfter}</strong>, i perioden <strong>{monthFrom.toLowerCase()}</strong> til <strong>{monthTo.toLowerCase()}</strong>.
        </p>
        <p>
          Hver dag er først normaliseret, så det samlede forbrug svarer til 100%. Derefter vises den gennemsnitlige procentvise fordeling for hver time.
        </p>
        <p>
          To søjler pr. time viser, om en større eller mindre andel af forbruget nu ligger i den pågældende time - uanset om det samlede forbrug er steget eller faldet.
        </p>
      </>
    );
  }

  if (chartType === "monthly" && valueTypeMonthly === "averageHourlyProportionConsumption") {
    return (
      <>
        <strong>ℹ️ Gns. andel af det daglige forbrug (%):</strong>
        <p>
          Grafen viser den <em>gennemsnitlige forbrugsandel</em> (%) pr. time i hver last-kategori (<span style={{color:'#008550'}}>lav</span>, <span style={{color:'#EFB700'}}>høj</span>, <span style={{color:'#B71D13'}}>spids</span>) per dag.
        </p>
        <p>
          F.eks. betyder 3.42%, at én lavlast-time i gennemsnit udgør 3.42% af døgnets samlede elforbrug, for den pågældende måned.
        </p>
        <p>
          Dette gør det lettere at sammenligne tidsrum, selvom de indeholder et forskelligt antal timer.
        </p>
      </>
    );
  }

  if (chartType === "monthly" && valueTypeMonthly === "averageHourlyTotalConsumption") {
    return (
      <>
        <strong>ℹ️ Gns. forbrug pr. time (kWh):</strong>
        <p>
          Grafen viser det <em>gennemsnitlige forbrug</em> (kWh) pr. time i hver last-kategori (<span style={{color:'#008550'}}>lav</span>, <span style={{color:'#EFB700'}}>høj</span>, <span style={{color:'#B71D13'}}>spids</span>) per måned.
        </p>
        <p>
          F.eks. betyder 7759 kWh, at én lavlast-time i gennemsnit bruger 7759 kWh.
        </p>
        <p>
          Dette gør det lettere at sammenligne tidsrum, selvom de indeholder et forskelligt antal timer.
        </p>
      </>
    );
  }

  return null;
};



return (
  <div className="chart-container">
    {/* Floating toggle/info box */}
    <div className={`info-toggle-wrapper ${showInfo ? "expanded" : ""}`} ref={infoRef}>
      <button
        className="info-toggle-btn"
        onClick={() => setShowInfo(prev => !prev)}
      >
        {showInfo ? "❌ Skjul info" : "ℹ️ Vis info"}
      </button>
      {showInfo && (
        <div className="info-box-floating">
          {renderInfoText()}
        </div>
      )}
    </div>

{/* Selector panel */}
<div className={`selector-grid ${chartType === "percentage" ? "is-percentage" : "is-monthly"}`}>
  {/* Col 1: chart type (and an optional invisible spacer ONLY in percentage mode) */}
  <div className="selector-col">
    <ChartSelector selected={chartType} setSelected={setChartType} />
    {chartType === "percentage" && <div className="spacer" aria-hidden />}
  </div>

  {/* Col 2: From (year above month) */}
  <div className="selector-col">
    <YearSelector selected={yearBefore} setSelected={setYearBefore} label="Fra år" />
    {chartType === "percentage" && (
      <MonthSelector selected={monthFrom} setSelected={setMonthFrom} label="Fra måned" />
    )}
  </div>

  {/* Col 3: To (year above month) */}
  <div className="selector-col">
    <YearSelector selected={yearAfter} setSelected={setYearAfter} label="Til år" />
    {chartType === "percentage" && (
      <MonthSelector selected={monthTo} setSelected={setMonthTo} label="Til måned" />
    )}
  </div>
</div>
    <div className="chart-area">
      {chartType === "percentage" ? (
        <PercentageChangeChart
          dataBefore={valueTypeByHour === 'averageHourlyProportionConsumption' ? proportionsBefore : averagesBefore}
          dataAfter={valueTypeByHour === 'averageHourlyProportionConsumption' ? proportionsAfter : averagesAfter}
          percentChanges={proportionChangePerHourPercent}
          averageDiffs={averageDiffPerHour}
          valueType={valueTypeByHour}
          setValueType={setValueTypeByHour}
          yearBefore={yearBefore}
          yearAfter={yearAfter}
          monthFrom={monthFrom}
          monthTo={monthTo}
        />
      ) : (
        <MonthlyLoadLineChart
  monthlyData={monthlyData} 
  valueType={valueTypeMonthly}
  setValueType={setValueTypeMonthly}
/>
      )}
    </div>
  </div>
);
};

export default ChartContainer;
